import { useState, useMemo } from 'react';
import { Bell, Send, Users, Building2, Radio } from 'lucide-react';
import type { NotificationChannel, TenantAdminRecipient } from '@yawi/types';
import { Button, Input, Select, Textarea, Card, Spinner } from '@/core/ui/components';
import { useConfirmation } from '@/app/providers';
import { useAdminNotifications } from '../hooks/useAdminNotifications';

type SendMode = 'single' | 'bulk' | 'tenant' | 'broadcast';

const CHANNEL_OPTIONS: { value: NotificationChannel; label: string }[] = [
  { value: 'IN_APP', label: 'In-App' },
  { value: 'PUSH', label: 'Push' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'SMS', label: 'SMS' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
];

export function NotificationsPage() {
  const { confirm } = useConfirmation();
  const { tenantAdmins, isLoading, isSending, send, sendBulk, sendToTenant, broadcast } = useAdminNotifications();

  const [sendMode, setSendMode] = useState<SendMode>('single');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [channels, setChannels] = useState<NotificationChannel[]>(['IN_APP']);

  // Get unique tenants for the tenant selector
  const uniqueTenants = useMemo(() => {
    const tenantMap = new Map<string, { id: string; name: string; slug: string }>();
    tenantAdmins.forEach((admin) => {
      if (!tenantMap.has(admin.tenantId)) {
        tenantMap.set(admin.tenantId, {
          id: admin.tenantId,
          name: admin.tenantName,
          slug: admin.tenantSlug,
        });
      }
    });
    return Array.from(tenantMap.values());
  }, [tenantAdmins]);

  const toggleChannel = (channel: NotificationChannel) => {
    setChannels((prev) =>
      prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel]
    );
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const resetForm = () => {
    setTitle('');
    setBody('');
    setSelectedUserId('');
    setSelectedUserIds([]);
    setSelectedTenantId('');
    setChannels(['IN_APP']);
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      return;
    }

    try {
      switch (sendMode) {
        case 'single':
          if (!selectedUserId) return;
          await send({
            userId: selectedUserId,
            title: title.trim(),
            body: body.trim(),
            channels,
          });
          break;

        case 'bulk':
          if (selectedUserIds.length === 0) return;
          await sendBulk({
            userIds: selectedUserIds,
            title: title.trim(),
            body: body.trim(),
            channels,
          });
          break;

        case 'tenant':
          if (!selectedTenantId) return;
          await sendToTenant(selectedTenantId, {
            title: title.trim(),
            body: body.trim(),
            channels,
          });
          break;

        case 'broadcast':
          const confirmed = await confirm({
            title: 'Broadcast a Todos',
            message: '¿Estás seguro de enviar esta notificación a TODOS los administradores de todos los tenants? Esta acción no se puede deshacer.',
            variant: 'warning',
            confirmText: 'Enviar a Todos',
          });
          if (!confirmed) return;
          await broadcast({
            title: title.trim(),
            body: body.trim(),
            channels,
          });
          break;
      }
      resetForm();
    } catch {
      // Error handled in hook
    }
  };

  const canSend = () => {
    if (!title.trim() || !body.trim() || channels.length === 0) return false;
    switch (sendMode) {
      case 'single':
        return !!selectedUserId;
      case 'bulk':
        return selectedUserIds.length > 0;
      case 'tenant':
        return !!selectedTenantId;
      case 'broadcast':
        return true;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
          <Bell className="w-6 h-6 text-purple-600 dark:text-purple-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Notificaciones</h1>
          <p className="text-gray-500 dark:text-gray-400">
            Enviar notificaciones a los administradores de tenants
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Mode Selection */}
        <Card className="lg:col-span-1">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="font-semibold text-gray-900 dark:text-white">Tipo de Envío</h2>
          </div>
          <div className="p-4 space-y-2">
            <button
              onClick={() => setSendMode('single')}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                sendMode === 'single'
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Send className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <div className="text-left">
                <p className="font-medium text-gray-900 dark:text-white">Individual</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Enviar a un admin</p>
              </div>
            </button>

            <button
              onClick={() => setSendMode('bulk')}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                sendMode === 'bulk'
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <div className="text-left">
                <p className="font-medium text-gray-900 dark:text-white">Múltiples</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Seleccionar varios admins</p>
              </div>
            </button>

            <button
              onClick={() => setSendMode('tenant')}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                sendMode === 'tenant'
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Building2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <div className="text-left">
                <p className="font-medium text-gray-900 dark:text-white">Por Tenant</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Todos los admins de un tenant</p>
              </div>
            </button>

            <button
              onClick={() => setSendMode('broadcast')}
              className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                sendMode === 'broadcast'
                  ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Radio className="w-5 h-5 text-red-600 dark:text-red-400" />
              <div className="text-left">
                <p className="font-medium text-gray-900 dark:text-white">Broadcast</p>
                <p className="text-sm text-red-500 dark:text-red-400">¡Enviar a TODOS!</p>
              </div>
            </button>
          </div>
        </Card>

        {/* Form */}
        <Card className="lg:col-span-2">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="font-semibold text-gray-900 dark:text-white">Componer Notificación</h2>
          </div>
          <div className="p-4 space-y-4">
            {/* Recipient Selection */}
            {sendMode === 'single' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Destinatario
                </label>
                {isLoading ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Spinner size="sm" /> Cargando administradores...
                  </div>
                ) : (
                  <Select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    options={[
                      { value: '', label: 'Seleccionar administrador...' },
                      ...tenantAdmins.map((admin) => ({
                        value: admin.id,
                        label: `${admin.name} - ${admin.tenantName} (${admin.email})`,
                      })),
                    ]}
                  />
                )}
              </div>
            )}

            {sendMode === 'bulk' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Seleccionar Destinatarios ({selectedUserIds.length} seleccionados)
                </label>
                {isLoading ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Spinner size="sm" /> Cargando administradores...
                  </div>
                ) : (
                  <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 space-y-1">
                    {tenantAdmins.map((admin) => (
                      <label
                        key={admin.id}
                        className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(admin.id)}
                          onChange={() => toggleUserSelection(admin.id)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-900 dark:text-white">{admin.name}</span>
                        <span className="text-sm text-gray-500">- {admin.tenantName}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {sendMode === 'tenant' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Seleccionar Tenant
                </label>
                <Select
                  value={selectedTenantId}
                  onChange={(e) => setSelectedTenantId(e.target.value)}
                  options={[
                    { value: '', label: 'Seleccionar tenant...' },
                    ...uniqueTenants.map((tenant) => ({
                      value: tenant.id,
                      label: `${tenant.name} (${tenant.slug})`,
                    })),
                  ]}
                />
              </div>
            )}

            {sendMode === 'broadcast' && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-red-700 dark:text-red-300 font-medium">
                  ⚠️ Esta notificación se enviará a TODOS los administradores de TODOS los tenants activos.
                </p>
              </div>
            )}

            {/* Title */}
            <Input
              label="Título"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Título de la notificación"
              maxLength={255}
              required
            />

            {/* Body */}
            <Textarea
              label="Mensaje"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Escribe el mensaje de la notificación..."
              rows={4}
              required
            />

            {/* Channels */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Canales de Envío
              </label>
              <div className="flex flex-wrap gap-2">
                {CHANNEL_OPTIONS.map((channel) => (
                  <button
                    key={channel.value}
                    type="button"
                    onClick={() => toggleChannel(channel.value)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      channels.includes(channel.value)
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {channel.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Send Button */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                onClick={handleSend}
                disabled={!canSend()}
                isLoading={isSending}
                leftIcon={<Send className="w-4 h-4" />}
                className={sendMode === 'broadcast' ? 'bg-red-600 hover:bg-red-700' : ''}
              >
                {sendMode === 'broadcast' ? 'Enviar Broadcast' : 'Enviar Notificación'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
